/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Action, AnyAction, Dispatch } from 'redux';
import {
  postAddressSuggestions,
  postZipcodeSuggestions,
  postSubmitPurchase,
  patchUpdate
} from 'services/ApiCheckoutPageService';
import { CustomerType } from 'models/CustomerType';
import { AddressInfo } from 'models/AddressInfo';
import { PaymentMethod } from 'models/PaymentMethod';
import { blur, change, getFormSyncErrors, resetSection } from 'redux-form';
import { PageState } from 'models/PageState';
import {
  checkoutForm,
  checkoutFormName,
  fieldStreet,
  fieldStreetAlternate,
  fieldZipcode,
  formatAddress,
  isContactInfoInvalid,
  isVouchersCoveringExpenses,
  useFreeOfChargePaymentMethod
} from './selector';
import batchAction from 'store/batching';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { PurchaseResponse } from 'models/PurchaseResponse';
import { debounceP, rm, setCookie, uid } from 'utils';
import { CartItem } from 'models/CartItem';
import { CheckoutFormData } from 'models/CheckoutFormData';
import { ThunkAction } from 'redux-thunk';
import { CheckoutPageModel, isCheckoutPage } from 'models/CheckoutPageModel';
import { resetDeliveryOptions } from 'views/CheckoutPage/DeliveryInformation/actions';
import { UpdateAddress, UpdateRequest } from 'models/UpdateRequest';
import { ThunkDispatch } from 'components/Shared/connect';
import { setPageLoadingStatus } from 'components/Pagelayout/actions';
import { LoadingStatus } from 'models/LoadingStatus';
import { handleServerError } from 'components/Shared/server';
import { isValidZipcode } from './themes/BGH/validation';
import { withCache } from 'utils/withCache';
import { CART_CHARGES } from 'components/Cart/actions';
import { resumeWidget, suspendWidget } from './components/deliveryWidget/util';
import { GtmAddContactInfo } from 'store/googleTagManager/actions';

export const CHECKOUT_UPDATE = 'CHECKOUT_UPDATE';
export const CHECKOUT_ADDRESS_INFO_SET = 'CHECKOUT_ADDRESS_INFO_SET';
export const CHECKOUT_ADDRESS_INFO_MULTIPLE = 'CHECKOUT_ADDRESS_MULTIPLE_RESPONSE';
export const CHECKOUT_MODAL_ADDRESS_TOO_LONG_NAME = 'CHECKOUT_ADDRESS_TOO_LONG_NAME';
export const CHECKOUT_HIDE_DELIVERY_OPTIONS = 'CHECKOUT_HIDE_DELIVERY_OPTIONS';
export const CHECKOUT_CART_ITEMS_SET = 'CHECKOUT_CART_ITEMS_SET';
export const CHECKOUT_CART_ITEMS_REMOVE = 'CHECKOUT_CART_ITEMS_REMOVE';
export const CHECKOUT_CART_ITEM_UPDATE = 'CHECKOUT_CART_ITEM_UPDATE';
export const CHECKOUT_CART_ITEM_QUANTITY_UPDATE = 'CHECKOUT_CART_ITEM_QUANTITY_UPDATE';
export const CHECKOUT_QUICKSTART_SET = 'CHECKOUT_QUICKSTART_SET';
export const CHECKOUT_SINGLEPRODUCT_SET = 'CHECKOUT_SINGLEPRODUCT_SET';

export interface CheckoutQuickStartParams {
  value: boolean;
}

export interface CheckoutAddressAutoParams {
  number: string;
  customerType: CustomerType;
}
export interface CheckoutAddressInfoParams {
  addressInfo: AddressInfo | AddressInfo[];
}

export type CheckoutPaymentMethodsParams = Partial<
  Pick<AddressInfo, 'customerType' | 'identityNumber' | 'hasDeliveryAddress'>
>;

interface CheckoutUpdateCartItemsParams {
  cartItem: CartItem;
  checkoutCartItemId?: string;
}

export interface AddressSuggestionsAction extends Action {
  addresses: SuggestedAddress[];
}

interface CheckoutCartItemParams {
  productId: string;
  cartItemId?: string;
  removed: boolean;
}

interface CheckoutCartItemSetParams {
  cartItems: CartItem[];
}

interface CheckoutQuantityUpdateParams {
  cartItemId: string;
  amount: number;
}

export type CheckoutUpdateAction = CheckoutPageModel & Action;
export type CheckoutAddressInfoAction = CheckoutAddressInfoParams & Action;
export type CheckoutPaymentMethodsAction = Action & {
  paymentMethods: PaymentMethod[];
};
export type CheckoutUpdateCartItemsAction = CheckoutUpdateCartItemsParams & Action;
export type CheckoutCartItemsAction = CheckoutCartItemParams & Action;
export type CheckoutSetCartItemsAction = CheckoutCartItemSetParams & Action;
export type CheckoutQuantityUpdateAction = CheckoutQuantityUpdateParams & Action;
export type CheckoutQuickStartAction = CheckoutQuickStartParams & Action;
export type CheckoutTooLongNameAction = Action & {
  address: AddressInfo;
};

export const quickStartSet = ({ value }: CheckoutQuickStartParams) =>
  change(checkoutFormName, 'quickStart', value);

export const singleProductSet = (value: boolean) => ({
  type: CHECKOUT_SINGLEPRODUCT_SET,
  value
});

export const setAddressDetails = (address: AddressInfo) => (dispatch: Dispatch) => {
  dispatch({ type: CHECKOUT_MODAL_ADDRESS_TOO_LONG_NAME, address } as CheckoutTooLongNameAction);

  if (address.isTooLongName) {
    return;
  }

  return dispatch(
    batchAction(
      blur(checkoutFormName, 'address', address),
      onBlurCheckout('address', Boolean(address))
    )
  );
};

export const setCheckoutCartItems = (args: CheckoutCartItemSetParams) => ({
  type: CHECKOUT_CART_ITEMS_SET,
  ...args
});

export const setCheckoutCartItemsRemoved = (args: CheckoutCartItemParams) => ({
  type: CHECKOUT_CART_ITEMS_REMOVE,
  ...args
});

export const setCheckoutCartItemUpdate = (args: CheckoutUpdateCartItemsParams) => ({
  type: CHECKOUT_CART_ITEM_UPDATE,
  ...args
});

export const setCheckoutCartItemQuantity = (args: CheckoutQuantityUpdateParams) => ({
  type: CHECKOUT_CART_ITEM_QUANTITY_UPDATE,
  ...args
});

export const hideDeliveryOptions = (): Action => ({ type: CHECKOUT_HIDE_DELIVERY_OPTIONS });

const FEEDBACK_DELAY = 1000;
export const checkoutRequestWrapper = (function () {
  const requests = new Set();
  let handler: number | undefined;
  const done = (id: string, dispatch: Dispatch) => {
    requests.delete(id);
    if (requests.size === 0) {
      window.clearTimeout(handler);
      dispatch(setPageLoadingStatus(LoadingStatus.Completed));
      resumeWidget();
    }
  };
  return <T>(promise: Promise<T>, dispatch: Dispatch): Promise<T> => {
    const id = uid();
    if (requests.size === 0) {
      handler = window.setTimeout(() => {
        dispatch(setPageLoadingStatus(LoadingStatus.Fetching));
        suspendWidget();
      }, FEEDBACK_DELAY);
    }
    requests.add(id);
    return promise
      .then(r => {
        done(id, dispatch);
        return r;
      })
      .catch(r => {
        done(id, dispatch);
        handleServerError(dispatch)(r);
        return Promise.reject(r);
      });
  };
})();

const POSTUPDATE_INTERVAL = 500;
function aggregateDebounce(wait: number) {
  let timeout: number | undefined;
  let pendingArgs = {};
  return function () {
    timeout && window.clearTimeout(timeout);
    pendingArgs = { ...pendingArgs };
    return new Promise(resolve => {
      timeout = window.setTimeout(() => {
        resolve(pendingArgs);
        pendingArgs = [];
      }, wait);
    });
  };
}
const debouncedPutUpdate = aggregateDebounce(POSTUPDATE_INTERVAL);
const ZIPCODE_FIELD = 'address.zipCode';
const ZIPCODE_ALTERNATE_FIELD = 'address.deliveryAddress.zipCode';

type GetAddressRequestChange = {
  state: PageState;
};
const getAddressRequestChange = ({ state }: GetAddressRequestChange) => {
  const addressForm = checkoutForm(state, 'address', 'quickStart');

  if (!addressForm) {
    return {};
  }

  return {
    address: addressForm.address,
    quickStart: addressForm.quickStart
  };
};

const isTooLongName = (
  firstName: string | undefined,
  lastName: string | undefined,
  maxLength: number
) => {
  return (firstName?.trim().length ?? 0) + (lastName?.trim().length ?? 0) > maxLength;
};

const trimAddress = (address: UpdateAddress) => {
  return {
    ...address,
    firstName: address.firstName?.trim(),
    lastName: address.lastName?.trim(),
    ...(address.deliveryAddress && {
      deliveryAddress: {
        ...address.deliveryAddress,
        firstName: address.deliveryAddress.firstName?.trim(),
        lastName: address.deliveryAddress.lastName?.trim()
      }
    })
  };
};

interface EmitAddContactInfoEvent {
  getState: () => PageState;
  dispatch: ThunkDispatch;
}
const emitAddContactInfoEvent = ({ getState, dispatch }: EmitAddContactInfoEvent) => {
  const state = getState();
  const address = checkoutForm(state, 'address') as AddressInfo;
  const errors = getFormSyncErrors('checkout')(state) as CheckoutFormData;
  const needsCertificateId = state.cart.needsCertificateId;
  const isInvalid = isContactInfoInvalid(address, errors, needsCertificateId);

  const hasEmittedKey = `hasEmitted`;
  const shouldEmitEvent = withCache(cache => {
    const isCached = cache.get(hasEmittedKey);
    if (isCached || isInvalid) return false;
    cache.set(hasEmittedKey, true);
    return true;
  });

  if (!shouldEmitEvent) return;
  dispatch(GtmAddContactInfo());
};

export const onBlurCheckout =
  (field?: string, value?: string | number | boolean) =>
  (dispatch: ThunkDispatch, getState: () => PageState) => {
    if (!field) {
      return;
    }

    emitAddContactInfoEvent({ getState, dispatch });

    const isSameValue = withCache(cache => {
      const isEntireForm = field === 'address';
      if (isEntireForm) {
        return false;
      }

      const userChangedCustomerType = field === 'address.customerType';
      if (userChangedCustomerType) {
        cache.reset();
        return false;
      }

      const isSameValue = cache.exists(field, value);
      cache.set(field, value);
      return isSameValue;
    });

    if (isSameValue) {
      return;
    }

    void debouncedPutUpdate().then(() => {
      const state = getState();
      const addressRequestChange = getAddressRequestChange({ state });

      if (addressRequestChange.address && isCheckoutPage(state.currentPage)) {
        const firstName = addressRequestChange.address.firstName;
        const lastName = addressRequestChange.address.lastName;
        const deliveryAddress = addressRequestChange.address.deliveryAddress;
        const {
          initialValues: {
            address: { maxNameLength }
          }
        } = state.currentPage;

        const isAddressNameTooLong = isTooLongName(firstName, lastName, maxNameLength);
        const isDeliveryAddressNameTooLong =
          deliveryAddress &&
          isTooLongName(deliveryAddress.firstName, deliveryAddress.lastName, maxNameLength);

        if (isAddressNameTooLong || isDeliveryAddressNameTooLong) {
          return;
        }
      }
      // Never store invalid zipCodes, the SOA cant handle it.
      if (
        (field === ZIPCODE_FIELD || field === ZIPCODE_ALTERNATE_FIELD) &&
        !isValidZipcode(value as string)
      ) {
        return;
      }

      dispatch(
        updateCheckout({
          ...addressRequestChange,
          address: trimAddress(addressRequestChange.address)
        })
      );
    });
  };

export type UpdateRequestParams = Omit<UpdateRequest, 'address'> &
  Partial<Pick<UpdateRequest, 'address'>>;
export const updateCheckout =
  (change: UpdateRequestParams) => (dispatch: ThunkDispatch, getState: () => PageState) => {
    const { currentPage } = getState();
    if (!isCheckoutPage(currentPage)) {
      return;
    }

    const isSameValue = withCache(cache => {
      const keys = Object.keys(change).join('.');
      const value = JSON.stringify(change);
      const isSameValue = cache.exists(keys, value);
      cache.set(keys, value);
      return isSameValue;
    });

    if (isSameValue) {
      return;
    }

    const shouldLoadDeliveries = 'address' in change;
    if (shouldLoadDeliveries) {
      dispatch(hideDeliveryOptions());
    }
    void checkoutRequestWrapper(patchUpdate(change), dispatch).then(page => {
      dispatch(
        batchAction(
          { type: CART_CHARGES, ...page },
          { type: CHECKOUT_UPDATE, ...page },
          setDefaultPaymentMethod(page),
          resetDeliveryOptions(page),
          setPageLoadingStatus(LoadingStatus.Completed)
        )
      );
    });
  };

export const setDefaultPaymentMethod =
  ({ paymentMethods }: CheckoutPageModel) =>
  (dispatch: Dispatch, getState: () => PageState) => {
    const state = getState();
    const { currentPage, cart } = state;

    if (!isCheckoutPage(currentPage) || !paymentMethods) {
      return;
    }

    const invoiceCommonName = 'svea_payment_invoice';
    const cardCommonName = 'svea_payment_card';
    const voucherCommonName = 'payment_voucher';
    const freeOfChargeCommonName = 'freeofcharge';
    const currentPaymentMethod: string = checkoutForm(state, 'payment.method');

    const isInvoice = ({ commonName }: PaymentMethod) => commonName.includes(invoiceCommonName);
    const isCard = ({ commonName }: PaymentMethod) => commonName === cardCommonName;
    const selectedMethodExists = () => {
      const current = paymentMethods.find(m => m.commonName === currentPaymentMethod);
      return current !== undefined;
    };

    if (isVouchersCoveringExpenses(cart)) {
      dispatch(change(checkoutFormName, 'payment.method', voucherCommonName));
      return;
    }

    if (useFreeOfChargePaymentMethod(cart)) {
      dispatch(change(checkoutFormName, 'payment.method', freeOfChargeCommonName));
      return;
    }

    if (selectedMethodExists()) {
      // prevent resetting the payment method if the selected one exists
      return;
    }

    const defaultMethod = paymentMethods.find(isInvoice) || paymentMethods.find(isCard);
    const name = defaultMethod && defaultMethod.commonName;
    dispatch(change(checkoutFormName, 'payment.method', name));
  };

export const cartChanged = () => (dispatch: ThunkDispatch) => {
  void checkoutRequestWrapper(patchUpdate({ cartChanged: true }), dispatch).then(page =>
    dispatch(
      batchAction(
        { type: CHECKOUT_UPDATE, ...page },
        setDefaultPaymentMethod(page),
        resetDeliveryOptions(page)
      )
    )
  );
};

export const deliveryAddressChanged =
  (checked: boolean, name?: string) => (dispatch: ThunkDispatch) => {
    if (checked) {
      dispatch(batchAction(hideDeliveryOptions(), resetSection(checkoutFormName, 'deliveries')));
    } else {
      dispatch(resetSection(checkoutFormName, 'address.deliveryAddress'));
    }
    dispatch(onBlurCheckout(name, checked));
  };

// ------------------

export const CHECKOUT_SUGGESTED_ADDRESSES_LOADED = 'CHECKOUT_SUGGESTED_ADDRESSES_LOADED';
export const CHECKOUT_SUGGESTED_CLEAR = 'CHECKOUT_SUGGESTED_CLEAR';
export const CHECKOUT_SUGGESTED_FIELD = 'CHECKOUT_SUGGESTED_FIELD';
export const CHECKOUT_PURCHASE_RESPONSE = 'CHECKOUT_PURCHASE_RESPONSE';
export const CHECKOUT_PURCHASE_CLOSE_MODAL = 'CHECKOUT_PURCHASE_CLOSE_MODAL';
export const CHECKOUT_PURCHASE_START = 'CHECKOUT_PURCHASE_START';

export interface SuggestedAddressesAction extends Action {
  addresses: SuggestedAddress[];
}

export interface SuggestedFieldAction extends Action {
  fieldName: string;
}

const TIMEOUT = 300;
const MIN_INPUT_LENGHT = 3;
const addressSuggestions = debounceP(postAddressSuggestions, TIMEOUT);
const zipCodeSuggestions = debounceP(postZipcodeSuggestions, TIMEOUT);
export const suggestionFieldValueChanged = (input: string, field: string) => {
  return (dispatch: Dispatch) => {
    if (input.length < MIN_INPUT_LENGHT) {
      dispatch(suggestionClear());
      return;
    }

    dispatch(suggestionSelectedField(field));

    const url =
      field === fieldStreet || field === fieldStreetAlternate
        ? addressSuggestions
        : zipCodeSuggestions;

    void url(input).then(response => {
      dispatch(addressSuggestionsLoaded(response));
    });
  };
};

export const suggestionSelectedField = (fieldName: string) => ({
  type: CHECKOUT_SUGGESTED_FIELD,
  fieldName
});

export const addressSuggestionsLoaded = (addresses?: SuggestedAddress[]) => ({
  type: CHECKOUT_SUGGESTED_ADDRESSES_LOADED,
  addresses
});

export const suggestionClear = () => ({
  type: CHECKOUT_SUGGESTED_CLEAR
});

export const addressSelected = (address: SuggestedAddress, field: string) => {
  return (dispatch: ThunkDispatch) => {
    const prefix =
      field === fieldStreet || field === fieldZipcode ? 'address' : 'address.deliveryAddress';
    let actions: (ThunkAction<unknown, never, never, AnyAction> | AnyAction)[] = [];
    if (address.street) {
      const field = `${prefix}.street`;
      const value = formatAddress(address);
      actions = [...actions, blur(checkoutFormName, field, value), onBlurCheckout(field, value)];
    }
    actions = [
      ...actions,
      blur(checkoutFormName, `${prefix}.zipCode`, address.postalcode),
      onBlurCheckout(`${prefix}.zipCode`, address.postalcode),
      blur(checkoutFormName, `${prefix}.city`, address.locality),
      onBlurCheckout(`${prefix}.city`, address.locality)
    ];
    dispatch(batchAction(suggestionClear(), ...actions));
  };
};

export interface PurchaseResponseAction extends Action {
  response: PurchaseResponse;
}

export const purchaseResponseLoaded = (response: PurchaseResponse) => ({
  type: CHECKOUT_PURCHASE_RESPONSE,
  response
});

export const purchaseStarted = () => ({
  type: CHECKOUT_PURCHASE_START,
  response: { message: rm('checkout_order_processing_step2'), valid: true }
});

export const closePurchModal = () => ({
  type: CHECKOUT_PURCHASE_CLOSE_MODAL
});

const PAYMENT_TYPES = [
  'SveaWebPay',
  'prepayment',
  'IntercompanyPayment',
  'VoucherPayment',
  'freeofcharge'
];

export const submitPurchase = (request: CheckoutFormData) => {
  return (dispatch: Dispatch, getState: () => PageState) => {
    dispatch(purchaseStarted());
    const { currentPage } = getState();

    if (!isCheckoutPage(currentPage)) {
      return;
    }

    return postSubmitPurchase(request)
      .then(response => {
        dispatch(purchaseResponseLoaded(response));

        const { valid, content } = response;
        const isError = content?.includes('error');
        if (!valid || isError) return;

        setCookie('pushOrder', '1');
        const orderInfo = getOrderInfo(content);

        const isValidated = getState().appShell.isValidated;
        if (isValidated && orderInfo.isBankId) {
          window.location.href = `/orders/${orderInfo.orderId}`;
          return;
        }

        const shouldRedirectToReceipt = PAYMENT_TYPES.find(paymentType =>
          content.includes(paymentType)
        );
        if (shouldRedirectToReceipt) {
          const receiptBaseUrl = getState().appShell.links.receipt;
          window.location.href = `${receiptBaseUrl}?orderId=${orderInfo.orderId}&orderIdHash=${orderInfo.orderIdHash}`;
          return;
        }

        const formWrapper = document.createElement('div');
        formWrapper.innerHTML = content;
        const form = formWrapper.querySelector<HTMLFormElement>('#PaymentForm');
        if (form === null) return;

        document.body.appendChild(form);
        form.submit();
      })
      .catch(err => {
        dispatch(closePurchModal());
        handleServerError(dispatch)(err);
      });
  };
};

const getOrderInfo = (content: string) => {
  const CONTENT_SEPARATOR = ';';
  const orderParts = content.split(CONTENT_SEPARATOR);

  return {
    orderId: orderParts[1],
    isBankId: orderParts[2] === 'True',
    orderIdHash: orderParts[3]
  };
};
